import { Children, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { brand } from "../../../theme/theme";

const arrowSx = (side, offset, size) => ({
  position: "absolute",
  top: "50%",
  [side]: offset,
  transform: "translateY(-50%)",
  zIndex: 2,
  width: size,
  height: size,
  backgroundColor: "#fff",
  color: brand.pinkDark,
  border: "1px solid #E5E7EB",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
  "&:hover": { backgroundColor: brand.soft },
});

/**
 * Horizontal row of cards. Shows as many as fit (`itemWidth`, responsive) and
 * arrows to page through the rest; also scrolls with touch/trackpad. Both arrows
 * show whenever the row scrolls.
 * With `minItemWidth` (px) the cards stretch to always fill the row: as many
 * as fit at that minimum width, and arrows only when there are more; then the
 * arrows get their own gutter at each side instead of covering the cards.
 * With `tabs` ({ label, icon } per card) a row of chips above says which cards
 * are in view and jumps to the others; it only shows while the row scrolls,
 * or always with `tabsTrailing` (a node after the chips, e.g. an "add" button).
 * `activeIndex` scrolls that card into view when it changes (e.g. a card just added).
 */
export function ArrowCarousel({
  children,
  itemWidth = { xs: "88%", sm: "calc(50% - 8px)", lg: "calc(33.333% - 11px)" },
  gap = 2,
  minItemWidth = null,
  arrowOffset = { xs: 4, md: -14 },
  arrowSize = 38,
  itemSx = {},
  sx = {},
  ariaLabel = "Contenidos",
  tabs = null,
  tabsTrailing = null,
  activeIndex = null,
}) {
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  // Width of the whole carousel, not the track: the track shrinks when the arrow
  // gutter appears, and measuring it would keep the fewer-cards layout after a resize.
  const [rootWidth, setRootWidth] = useState(0);
  const [visible, setVisible] = useState([]); // per card: at least half of it in view
  const items = Children.toArray(children).filter(Boolean);

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanPrev(track.scrollLeft > 4);
    setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
    if (rootRef.current) setRootWidth(rootRef.current.clientWidth);
    const trackRect = track.getBoundingClientRect();
    const next = Array.from(track.children).map((child) => {
      const rect = child.getBoundingClientRect();
      const shown = Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left);
      return rect.width > 0 && shown >= rect.width / 2;
    });
    setVisible((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, []);

  useEffect(() => {
    updateArrows();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateArrows);
    observer.observe(track);
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [updateArrows, items.length]);

  // Auto-fit: n cards of at least `minItemWidth` share the whole row (gap in theme units of 8px).
  // With a gutter the arrows sit centered in it, clear of the cards.
  const gutterPx = arrowSize + 16;
  let resolvedItemWidth = itemWidth;
  let arrowGutter = false;
  if (minItemWidth && rootWidth > 0 && items.length > 0) {
    const gapPx = gap * 8;
    const fitIn = (width) => Math.max(1, Math.floor((width + gapPx) / (minItemWidth + gapPx)));
    let perView = fitIn(rootWidth);
    if (items.length > perView) {
      // Not all fit: the arrows take a gutter at each side, so fit into what is left.
      arrowGutter = true;
      perView = fitIn(rootWidth - 2 * gutterPx);
    }
    perView = Math.min(items.length, perView);
    resolvedItemWidth = `calc((100% - ${(perView - 1) * gapPx}px) / ${perView})`;
  }

  // Re-check the arrows once the widths are applied.
  useEffect(() => {
    updateArrows();
  }, [resolvedItemWidth, updateArrows]);

  const page = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: "smooth" });
  };

  const scrollToItem = (index) => {
    const track = trackRef.current;
    const child = track?.children[index];
    if (!child) return;
    const left = track.scrollLeft + child.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left, behavior: "smooth" });
  };

  // Bring the active card into view if it is (partly) hidden; waits for the new widths.
  useEffect(() => {
    if (activeIndex == null) return undefined;
    const frame = requestAnimationFrame(() => {
      const track = trackRef.current;
      const child = track?.children[activeIndex];
      if (!child) return;
      const trackRect = track.getBoundingClientRect();
      const rect = child.getBoundingClientRect();
      if (rect.left < trackRect.left - 1 || rect.right > trackRect.right + 1) scrollToItem(activeIndex);
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, items.length, resolvedItemWidth]);

  const resolvedArrowOffset = arrowGutter ? 0 : arrowOffset;
  const scrollable = canPrev || canNext;
  const showTabs = (scrollable || Boolean(tabsTrailing)) && tabs?.length === items.length;

  return (
    <Box ref={rootRef} sx={{ position: "relative", minWidth: 0, ...sx }}>
      {showTabs && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap", mx: arrowGutter ? `${gutterPx}px` : 0 }}>
          {tabs.map((tab, index) => {
            const inView = Boolean(visible[index]);
            return (
              <Box
                key={index}
                component="button"
                type="button"
                onClick={() => scrollToItem(index)}
                aria-current={inView || undefined}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "999px",
                  border: "1px solid",
                  borderColor: inView ? brand.ink : "#E5E7EB",
                  backgroundColor: inView ? "#F1F5F9" : "#fff",
                  color: inView ? brand.ink : "#6B7280",
                  fontSize: "0.8125rem",
                  fontWeight: inView ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: brand.ink, color: brand.ink },
                }}
              >
                {tab.icon}
                {tab.label}
              </Box>
            );
          })}
          {tabsTrailing}
        </Box>
      )}
      {scrollable && (
        <IconButton aria-label="Anteriores" onClick={() => page(-1)} sx={arrowSx("left", resolvedArrowOffset, arrowSize)}>
          <ChevronLeft size={Math.round(arrowSize * 0.52)} />
        </IconButton>
      )}
      <Box
        ref={trackRef}
        role="list"
        aria-label={ariaLabel}
        onScroll={updateArrows}
        sx={{
          display: "flex",
          gap,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          pb: 0.5,
          mx: arrowGutter ? `${gutterPx}px` : 0,
        }}
      >
        {items.map((child, index) => (
          <Box
            key={child.key ?? index}
            role="listitem"
            sx={{
              flex: "0 0 auto",
              width: resolvedItemWidth,
              scrollSnapAlign: "start",
              display: "flex",
              minWidth: 0,
              ...itemSx,
            }}
          >
            {child}
          </Box>
        ))}
      </Box>
      {scrollable && (
        <IconButton aria-label="Siguientes" onClick={() => page(1)} sx={arrowSx("right", resolvedArrowOffset, arrowSize)}>
          <ChevronRight size={Math.round(arrowSize * 0.52)} />
        </IconButton>
      )}
    </Box>
  );
}

/** Previous/next buttons with a counter, to walk through pieces one at a time. */
export function PagerArrows({ index, total, onChange, label = "contenido", sx = {} }) {
  if (total <= 1) return null;
  const buttonSx = {
    width: 32,
    height: 32,
    border: "1px solid #E5E7EB",
    backgroundColor: "#fff",
    color: "#374151",
    "&:hover": { backgroundColor: brand.soft, color: brand.pinkDark },
    "&.Mui-disabled": { opacity: 0.4, backgroundColor: "#fff" },
  };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, ...sx }}>
      <IconButton
        aria-label={`${label} anterior`}
        size="small"
        disabled={index <= 0}
        onClick={() => onChange(index - 1)}
        sx={buttonSx}
      >
        <ChevronLeft size={17} />
      </IconButton>
      <Box component="span" sx={{ fontSize: "0.8125rem", color: "#6B7280", minWidth: 44, textAlign: "center" }}>
        {index + 1} de {total}
      </Box>
      <IconButton
        aria-label={`${label} siguiente`}
        size="small"
        disabled={index >= total - 1}
        onClick={() => onChange(index + 1)}
        sx={buttonSx}
      >
        <ChevronRight size={17} />
      </IconButton>
    </Box>
  );
}
